/* eslint-disable lfds/no-raw-token-strings -- --lfds-skeleton-* are internal custom properties from #utils/skeleton.css, not design tokens */
import tokens from '@lansforsakringar/core-tokens'
import { css } from 'lit'

import { vars, scoped, declare, override } from '#utils/styles'

const locals = scoped('input', [
	'background_color',
	'border_radius',
	'border_color',
	'border_width',
	'padding',
	'height',
	'color'
] as const)

export default css`
	:host {
		${declare(locals, {
			background_color: tokens.color_background_secondary,
			border_radius: tokens.component_text_input_border_radius,
			border_color: tokens.color_border_focus,
			border_width: tokens.border_width_default,
			padding: tokens.spacing_sm,
			height: tokens.spacing_4xl,
			color: tokens.color_text_primary
		})};

		display: flex;
		width: 100%;
		flex-direction: column;
		color: ${vars(locals.color)};
	}

	.label {
		font-size: ${vars(tokens.text_size_label_default)};
		font-weight: ${vars(tokens.font_weight_semibold)};
		line-height: ${vars(tokens.line_height_label_default)};
		text-wrap: balance;
	}

	.box {
		display: flex;
		align-items: center;
		overflow: hidden;
		box-sizing: border-box;
		height: calc(
			${vars(locals.height)} + (${vars(tokens.border_width_default)} * 2)
		);
		background: ${vars(locals.background_color)};
		border-radius: ${vars(locals.border_radius)};
		border: ${vars(locals.border_width)} solid;
		color: ${vars(locals.color)};
		border-color: ${vars(locals.border_color)};
		transition: 125ms var(--ease-in);
		transition-property: border-color, border-width;

		/* Make the border gray when it's empty */
		&:has(.input:where(:placeholder-shown)) {
			${override(locals.border_color, tokens.color_border_tertiary)};
		}

		&:has(
			.input:-webkit-autofill,
			.input:-webkit-autofill:hover,
			.input:-webkit-autofill:focus,
			.input:-webkit-autofill:active,
			.input:autofill
		) {
			background: ${vars(tokens.color_background_highlight_primary)};
		}

		&.autofilled {
			background: ${vars(tokens.color_background_highlight_primary)};
		}

		/* Focus and hover state */
		&:has(.input:where(:hover, :focus)) {
			${override(locals.border_color, tokens.color_border_focus)};
			${override(locals.border_width, tokens.border_width_focus)};
		}

		/* Active/pressed state */
		&:has(.input:active) {
			${override(locals.border_width, tokens.border_width_emphasis)};
			transition-timing-function: var(--ease-out);
			outline: 0;
		}

		&.focus-visible:has(.input:focus-visible) {
			outline: var(--focus-outline);
			outline-offset: var(--focus-outline-offset);
		}

		/* Invalid state */
		&.invalid {
			${override(locals.border_color, tokens.color_border_error)};
		}

		/* Loading state — field/border/icons stay visible, only the value shimmers */
		&.loading {
			pointer-events: none;
		}
	}

	.affix {
		line-height: calc(21 / 16);
		padding-inline: ${vars(locals.padding)};
		font-size: 1rem;
		font-weight: ${vars(tokens.font_weight_semibold)};
		cursor: text;
		color: inherit;
		user-select: none;

		&.prefix {
			padding-inline-end: 0;
		}

		&.suffix {
			padding-inline-start: 0;
		}
	}

	.input {
		flex: 1;
		padding: 0 ${vars(locals.padding)};
		height: 100%;
		min-height: ${vars(locals.height)};
		box-sizing: border-box;
		border: none;
		font-size: 1rem;
		line-height: calc(21 / 16);
		caret-color: ${vars(tokens.color_border_focus)};
		color: inherit;
		appearance: none;
		background: transparent;

		&:focus {
			outline: none;
		}
	}

	/*
	 * Loading — shimmer only a strip matching the input's own line-height
	 * (21/16 * 1rem), not the full field height. clip-path crops both the
	 * vertical strip and the horizontal value width (set inline per
	 * instance, since a native <input> can't host a sized ::after bar like
	 * other components) in one shape — its inset values match the content
	 * box exactly (same vertical centering, same start padding) so its
	 * round argument lands on the visible strip's actual corners. Rounding
	 * via background-clip: content-box alone doesn't work here: its corner
	 * radius is max(0, radius - padding), and padding on every side here
	 * is larger than the radius, clamping it to a hard 0.
	 */
	.box.loading .input {
		color: transparent;
		caret-color: transparent;
		background: var(--lfds-skeleton-background);
		clip-path: inset(
			calc((${vars(locals.height)} - (21 / 16 * 1rem)) / 2)
				calc(100% - var(--lfds-skeleton-width, 80%))
				calc((${vars(locals.height)} - (21 / 16 * 1rem)) / 2)
				${vars(locals.padding)} round var(--lfds-skeleton-border-radius)
		);
		animation: var(--lfds-skeleton-animation);
	}

	.input::-webkit-outer-spin-button,
	.input::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}

	.input:is(
		:-webkit-autofill,
		:-webkit-autofill:hover,
		:-webkit-autofill:focus,
		:-webkit-autofill:active,
		:autofill
	) {
		background: transparent !important;
		color: inherit !important;
		-webkit-text-fill-color: currentColor;
		/* Keep the input area aligned with wrapper autofill background without spilling over slot content. */
		-webkit-box-shadow: 0 0 0 1000px
			${vars(tokens.color_background_highlight_primary)} inset !important;
		box-shadow: 0 0 0 1000px ${vars(tokens.color_background_highlight_primary)}
			inset !important;
		animation: lfds-input-autofill-start 0.01s;
	}

	.input:not(:-webkit-autofill) {
		animation: lfds-input-autofill-cancel 0.01s;
	}

	@keyframes lfds-input-autofill-start {
		from {
			opacity: 1;
		}

		to {
			opacity: 1;
		}
	}

	@keyframes lfds-input-autofill-cancel {
		from {
			opacity: 1;
		}

		to {
			opacity: 1;
		}
	}

	.header {
		display: flex;
		justify-content: space-between;
		align-items: flex-end;
		gap: ${vars(tokens.spacing_sm)};
		color: ${vars(tokens.color_text_primary)};
		padding-block-end: ${vars(tokens.spacing_xs)};

		.header-text {
			display: flex;
			flex-direction: column;
		}
	}

	.help {
		cursor: default;
		font-size: ${vars(tokens.text_size_caption_default)};
		line-height: ${vars(tokens.line_height_caption_default)};
		color: ${vars(tokens.color_text_secondary)};

		&.counter {
			user-select: none;
		}

		&.warning {
			color: ${vars(tokens.color_text_error)};
		}
	}

	.validation {
		display: flex;
		align-items: flex-start;
		padding-block-start: ${vars(tokens.spacing_xs)};
		cursor: default;
		gap: ${vars(tokens.spacing_xs)};
		color: ${vars(tokens.color_text_error)};
		line-height: calc(18 / 14);
		font-size: 0.875rem;

		.icon {
			margin: -0.2rem 0 0 -0.15rem;
		}
	}
`
